import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getPreApprovalDetails,
    updatePreApproval,
    getVmsSettings,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_PRE_APPROVALS } from '../../../../Routing/routes';
import PreApprovalForm from './PreApprovalForm';
import VisitorNotFound from '../components/VisitorNotFound';
import { VmsLoader } from '../components/vms/VmsListShell';
import { toLocalInput, flatLabel } from '../vmsConstants';

const EditPreApproval = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { details, detailsLoading, saving } = useSelector(state => state.VmsReducer?.preApprovals) || {};
    const settings = useSelector(state => state.VmsReducer?.settings?.data);

    useEffect(() => {
        if (id) dispatch(getPreApprovalDetails({ id }));
    }, [dispatch, id]);

    useEffect(() => {
        if (societyId && !settings) dispatch(getVmsSettings({ societyId }));
    }, [dispatch, societyId, settings]);

    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_PRE_APPROVALS);

    // Only the editable fields are seeded. used_count and status are maintained
    // by the gate flow, and the ownership keys are rejected on update.
    const initial = useMemo(() => {
        if (!details) return null;
        return {
            visitor_name: details.visitor_name || '',
            visitor_phone: details.visitor_phone || '',
            visitor_email: details.visitor_email || '',
            purpose: details.purpose || 'PERSONAL',
            purpose_detail: details.purpose_detail || '',
            verification_mode: details.verification_mode || 'SIMPLE',
            valid_from: toLocalInput(details.valid_from),
            valid_until: toLocalInput(details.valid_until),
            max_usage_count: details.max_usage_count ?? 1,
            vehicle_number: details.vehicle_number || '',
            companion_count: details.companion_count ?? 0,
            instructions: details.instructions || '',
            flatLabel: flatLabel(details),
        };
    }, [details]);

    const handleSubmit = (payload) => {
        dispatch(updatePreApproval({ id, ...payload, onSuccess: backToList }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-w-full">
                <button
                    onClick={backToList}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-mb-3"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Pre-approvals
                </button>

                {detailsLoading && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-py-10">
                        <VmsLoader label="Loading pre-approval..." />
                    </div>
                )}

                {!detailsLoading && !details && <VisitorNotFound onBack={backToList} />}

                {!detailsLoading && details && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100">
                            <h1 className="tw-mb-0 tw-text-base tw-font-bold tw-text-gray-800">Edit Pre-approval</h1>
                            <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                For {details.visitor_name} · used {details.used_count ?? 0} of {details.max_usage_count ?? 1}
                            </p>
                        </div>
                        <div className="tw-p-5">
                            <PreApprovalForm
                                mode="edit"
                                societyId={societyId}
                                initial={initial}
                                submitting={saving}
                                maxDays={settings?.max_pre_approval_days}
                                onSubmit={handleSubmit}
                                onCancel={backToList}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditPreApproval;
